import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import {
  Zap, MapPin, DollarSign, BarChart3, ChevronDown, ArrowRight,
} from 'lucide-react';
import { fadeInUp, stagger } from './data';

const STEPS = [
  { step: '01', title: 'Sign Up', description: 'Create your account in under a minute. Start a free 7-day trial — no commitment required.', icon: Zap, color: 'amber' },
  { step: '02', title: 'Add Locations', description: 'Set up your locations, assign machines, and configure partner split agreements.', icon: MapPin, color: 'teal' },
  { step: '03', title: 'Track Collections', description: 'Record money in/out, upload receipts, and get digital signatures — all from any device.', icon: DollarSign, color: 'green' },
  { step: '04', title: 'View Reports', description: 'Instant P&L, tax reports, audit trails, and partner payouts — export to CSV anytime.', icon: BarChart3, color: 'rose' },
];

const COLOR_MAP = {
  amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', ring: 'ring-amber-500/20', num: 'from-amber-500 to-yellow-500' },
  teal: { bg: 'bg-teal-500/10', border: 'border-teal-500/30', text: 'text-teal-400', ring: 'ring-teal-500/20', num: 'from-teal-500 to-cyan-500' },
  green: { bg: 'bg-green-500/10', border: 'border-green-500/30', text: 'text-green-400', ring: 'ring-green-500/20', num: 'from-green-500 to-emerald-500' },
  rose: { bg: 'bg-rose-500/10', border: 'border-rose-500/30', text: 'text-rose-400', ring: 'ring-rose-500/20', num: 'from-rose-500 to-pink-500' },
};

export const HowItWorksSection = () => (
  <section id="how-it-works" aria-label="How It Works" className="py-20 md:py-32 bg-gradient-to-b from-black via-neutral-950 to-neutral-900 relative overflow-hidden" data-testid="how-it-works-section">
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-amber-500/5 rounded-full blur-[200px] pointer-events-none" />

    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
      <motion.div
        className="text-center max-w-3xl mx-auto mb-16 md:mb-20"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
        variants={stagger}
      >
        <motion.p variants={fadeInUp} className="text-amber-400 font-semibold mb-4 tracking-wider uppercase text-sm">
          GET STARTED IN MINUTES
        </motion.p>
        <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl font-bold mb-6 text-white">
          How It Works
        </motion.h2>
        <motion.p variants={fadeInUp} className="text-gray-400 text-lg">
          Go from sign-up to full visibility over your operation in four simple steps.
        </motion.p>
      </motion.div>

      <div className="relative">
        <div className="hidden lg:block absolute top-[72px] left-[calc(12.5%+24px)] right-[calc(12.5%+24px)] h-px">
          <motion.div
            className="h-full bg-gradient-to-r from-amber-500/60 via-yellow-500/60 to-amber-500/60"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, delay: 0.5, ease: 'easeOut' }}
            style={{ transformOrigin: 'left' }}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
          {STEPS.map((item, index) => {
            const c = COLOR_MAP[item.color];
            return (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="text-center relative group"
                data-testid={`how-it-works-step-${index + 1}`}
              >
                <div className={`relative w-[88px] h-[88px] mx-auto mb-6 rounded-full ${c.bg} ring-2 ${c.ring} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                  <div className={`absolute -top-2 -right-2 w-8 h-8 rounded-full bg-gradient-to-br ${c.num} flex items-center justify-center shadow-lg`}>
                    <span className="text-xs font-black text-black">{item.step}</span>
                  </div>
                  <item.icon className={`w-8 h-8 ${c.text}`} />
                </div>

                {index < 3 && (
                  <div className="lg:hidden flex justify-center my-2 sm:hidden">
                    <ChevronDown className="w-5 h-5 text-amber-500/40" />
                  </div>
                )}

                <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed max-w-[240px] mx-auto">{item.description}</p>
              </motion.div>
            );
          })}
        </div>
      </div>

      <motion.div
        className="text-center mt-14"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.8 }}
      >
        <Link to="/signup">
          <Button
            size="lg"
            className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-bold px-10 h-14 text-lg shadow-[0_0_30px_rgba(245,158,11,0.25)] hover:shadow-[0_0_40px_rgba(245,158,11,0.4)] transition-all"
            data-testid="how-it-works-cta"
          >
            Start Your Free Trial
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </Link>
      </motion.div>
    </div>
  </section>
);
